import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "text-2xl font-bold mb-6" }
const _hoisted_2 = { class: "text-black font-light text-sm leading-relaxed whitespace-pre-line" }
const _hoisted_3 = { class: "absolute inset-0 flex justify-between" }
const _hoisted_4 = {
  key: 0,
  class: "absolute -top-2 h-5 w-1 bg-white"
}
const _hoisted_5 = { class: "absolute top-4 text-brand-main text-xs" }
const _hoisted_6 = { class: "text-brand-main italic text-[12px] leading-tight text-center whitespace-nowrap mb-1" }
const _hoisted_7 = { class: "text-brand-main italic text-[12px] leading-tight text-center whitespace-nowrap mb-1" }
const _hoisted_8 = { class: "absolute top-8 left-1/2 -translate-x-1/2 flex flex-col items-center" }
const _hoisted_9 = { class: "bg-brand-main text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap -mt-[1px]" }

import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import DiamondDetailCaratSize from "@/components/diamonds/details/carat/DiamondDetailCaratSize.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'DiamondDetailCaratContent',
  props: {
    carat: { default: 0.5 },
    form: { default: 'BR' },
    width: { default: 5 },
    length: { default: 5 },
    mobileMode: { type: Boolean }
  },
  setup(__props: any) {

const { t } = useI18n();

const props = __props;

const isRange = computed(() => typeof props.carat === 'object' && props.carat !== null);

const minCarat = computed(() => {
  if (isRange.value) {
    return (props.carat as { min: number, max: number }).min;
  }
  return (props.carat as number) || 0;
});

const maxCarat = computed(() => {
  if (isRange.value) {
    return (props.carat as { min: number, max: number }).max;
  }
  return (props.carat as number) || 0;
});

const currentCarat = ref(minCarat.value);

const isMobile = ref(window.innerWidth < 769);
const updateMobile = () => {
  isMobile.value = window.innerWidth < 769;
};

const isMobileMode = computed(() => props.mobileMode || isMobile.value);

watch(minCarat, (newVal) => {
  currentCarat.value = newVal;
});

const displayDimensions = computed(() => {
  if (minCarat.value === 0) return { width: 0, length: 0 };
  const scale = Math.pow(currentCarat.value / minCarat.value, 1 / 3);
  return {
    width: props.width * scale,
    length: props.length * scale,
  };
});

const sliderRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);

const updateCaratFromEvent = (e: MouseEvent | TouchEvent) => {
  if (!sliderRef.value) return;
  const rect = sliderRef.value.getBoundingClientRect();
  const clientX = "touches" in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
  const offsetX = clientX - rect.left;
  let percentage = offsetX / rect.width;
  percentage = Math.max(0, Math.min(1, percentage));

  // 0 to 10 ct range, 0.1 ct steps
  const value = Math.round(percentage * 10 * 10) / 10;
  currentCarat.value = value;
};

const startDragging = (e: MouseEvent | TouchEvent) => {
  isDragging.value = true;
  updateCaratFromEvent(e);
};

const handleMove = (e: MouseEvent | TouchEvent) => {
  if (isDragging.value) {
    updateCaratFromEvent(e);
  }
};

const stopDragging = () => {
  isDragging.value = false;
};

onMounted(() => {
  window.addEventListener("mousemove", handleMove);
  window.addEventListener("mouseup", stopDragging);
  window.addEventListener("touchmove", handleMove, { passive: false });
  window.addEventListener("touchend", stopDragging);
  window.addEventListener("resize", updateMobile);
});

onUnmounted(() => {
  window.removeEventListener("mousemove", handleMove);
  window.removeEventListener("mouseup", stopDragging);
  window.removeEventListener("touchmove", handleMove);
  window.removeEventListener("touchend", stopDragging);
  window.removeEventListener("resize", updateMobile);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['flex flex-col items-center ', isMobileMode.value ? '' : 'gap-8 lg:flex-row'])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(['w-full text-left order-1', isMobileMode.value ? '' : 'lg:w-[34%] lg:order-1'])
    }, [
      _renderSlot(_ctx.$slots, "title", { currentCarat: currentCarat.value }, () => [
        _createElementVNode("h3", _hoisted_1, _toDisplayString(_unref(t)("components.diamonds.details.carat.title")) + ": " + _toDisplayString(_ctx.$n(currentCarat.value)) + " ct. ", 1)
      ]),
      _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(t)("components.diamonds.details.carat.text")), 1)
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass(['w-full py-20 px-4 order-3', isMobileMode.value ? '' : 'lg:w-[51%] lg:order-2'])
    }, [
      _createElementVNode("div", {
        class: "relative h-1 bg-brand-main/30 rounded-full cursor-pointer",
        ref_key: "sliderRef",
        ref: sliderRef,
        onMousedown: startDragging,
        onTouchstart: _withModifiers(startDragging, ["prevent"])
      }, [
        _createElementVNode("div", _hoisted_3, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(11, (i) => {
            return _createElementVNode("div", {
              key: i,
              class: "relative h-full flex flex-col items-center"
            }, [
              ( i !== 1 && i !== 11)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4))
                : _createCommentVNode("", true),
              _createElementVNode("span", _hoisted_5, _toDisplayString(i - 1), 1)
            ])
          }), 64))
        ]),
        _createElementVNode("div", {
          class: "absolute -top-14 -translate-x-1/2 flex flex-col items-center transition-all duration-300",
          style: _normalizeStyle({ left: (minCarat.value / 10) * 100 + '%' })
        }, [
          _createElementVNode("span", _hoisted_6, [
            _createTextVNode(_toDisplayString(isRange.value ? _unref(t)('components.diamonds.details.carat.min') : _unref(t)('components.diamonds.details.carat.your_diamond')), 1),
            _cache[0] || (_cache[0] = _createElementVNode("br", null, null, -1)),
            _createTextVNode(" " + _toDisplayString(_ctx.$n(minCarat.value)) + " ct. ", 1)
          ]),
          _cache[1] || (_cache[1] = _createElementVNode("div", { class: "w-[1px] h-6 border-l border-dashed border-brand-main" }, null, -1))
        ], 4),
        (isRange.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "absolute -top-14 -translate-x-1/2 flex flex-col items-center transition-all duration-300",
              style: _normalizeStyle({ left: (maxCarat.value / 10) * 100 + '%' })
            }, [
              _createElementVNode("span", _hoisted_7, [
                _createTextVNode(_toDisplayString(_unref(t)('components.diamonds.details.carat.max')), 1),
                _cache[2] || (_cache[2] = _createElementVNode("br", null, null, -1)),
                _createTextVNode(" " + _toDisplayString(_ctx.$n(maxCarat.value)) + " ct. ", 1)
              ]),
              _cache[3] || (_cache[3] = _createElementVNode("div", { class: "w-[1px] h-6 border-l border-dashed border-brand-main" }, null, -1))
            ], 4))
          : _createCommentVNode("", true),
        _createElementVNode("div", {
          class: "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-brand-main rounded-full z-20 shadow-sm",
          style: _normalizeStyle({ left: (currentCarat.value / 10) * 100 + '%' })
        }, [
          _createElementVNode("div", _hoisted_8, [
            _cache[4] || (_cache[4] = _createElementVNode("div", { class: "w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[8px] border-b-brand-main" }, null, -1)),
            _createElementVNode("div", _hoisted_9, _toDisplayString(_ctx.$n(currentCarat.value)) + " ct. ", 1)
          ])
        ], 4)
      ], 544)
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass(['w-full flex justify-center items-center order-2', isMobileMode.value ? '' : 'lg:w-[15%] lg:order-3'])
    }, [
      _createVNode(DiamondDetailCaratSize, {
        carat: currentCarat.value,
        form: __props.form,
        diamondWidth: displayDimensions.value.width,
        diamondLength: displayDimensions.value.length
      }, null, 8, ["carat", "form", "diamondWidth", "diamondLength"])
    ], 2)
  ], 2))
}
}

})